import { useState, useEffect } from "react";
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Select,
  Input,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  SettingsRegular,
  MailRegular,
  EditRegular,
  ArrowResetRegular,
} from "@fluentui/react-icons";
import { DEFAULT_ACCENT_COLOR } from "../utils/prefs";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const useStyles = makeStyles({
  settingsButton: {
    ":hover": {
      backgroundColor: "var(--aco-accent-bg)",
      color: "var(--aco-accent)",
    },
    ":hover .fui-Button__icon": { color: "var(--aco-accent)" },
    ":hover:active": {
      backgroundColor: "var(--aco-accent-bg)",
      color: "var(--aco-accent)",
    },
    ":hover:active .fui-Button__icon": { color: "var(--aco-accent)" },
  },
});

function normalizeHexInput(raw) {
  const trimmed = raw.trim();
  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}

export default function Header({
  uiLang,
  setUiLang,
  accentColor,
  setAccentColor,
  currentEmail,
  t,
}) {
  const styles = useStyles();
  const [hexInput, setHexInput] = useState(accentColor.toUpperCase());

  useEffect(() => {
    setHexInput(accentColor.toUpperCase());
  }, [accentColor]);

  const handleHexChange = (_, data) => {
    const next = data.value;
    setHexInput(next);
    const candidate = normalizeHexInput(next);
    if (HEX_RE.test(candidate)) {
      setAccentColor(candidate.toLowerCase());
    }
  };

  const handleHexBlur = () => {
    const candidate = normalizeHexInput(hexInput);
    if (HEX_RE.test(candidate)) {
      setHexInput(candidate.toUpperCase());
    } else {
      setHexInput(accentColor.toUpperCase());
    }
  };

  const isInvalid = !HEX_RE.test(normalizeHexInput(hexInput));

  const isCompose = currentEmail.mode === "compose";
  const contextLabel = isCompose
    ? currentEmail.recipient
      ? `${t("context.to")} ${currentEmail.recipient}`
      : t("context.draft")
    : currentEmail.sender
      ? `${t("context.from")} ${currentEmail.sender}`
      : t("context.select");

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "4px",
      }}
    >
      {/* 1. LE CONTEXTE DYNAMIQUE */}
      <div
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "left",
          padding: "0 1px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            maxWidth: "100%",
          }}
        >
          {isCompose ? (
            <EditRegular
              style={{ fontSize: "14px", color: tokens.colorNeutralForeground3, flexShrink: 0 }}
            />
          ) : (
            <MailRegular
              style={{ fontSize: "14px", color: tokens.colorNeutralForeground3, flexShrink: 0 }}
            />
          )}
          <Text
            size={100}
            style={{
              color: tokens.colorNeutralForeground2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontStyle: "italic",
            }}
          >
            {contextLabel}
          </Text>
        </div>
      </div>

      {/* 2. Rouage à droite */}
      <Popover trapFocus positioning="below-end">
        <PopoverTrigger disableButtonEnhancement>
          <Button
            icon={<SettingsRegular />}
            appearance="subtle"
            size="small"
            className={styles.settingsButton}
          />
        </PopoverTrigger>
        <PopoverSurface
          style={{
            padding: "12px",
            minWidth: "200px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: 600,
                color: tokens.colorNeutralForeground2,
                marginBottom: "4px",
              }}
            >
              {t("settings.ui_lang")}
            </label>
            <Select
              value={uiLang}
              onChange={(_, d) => setUiLang(d.value)}
              style={{ width: "100%" }}
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </Select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: 600,
                color: tokens.colorNeutralForeground2,
                marginBottom: "4px",
              }}
            >
              {t("settings.accent")}
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="color"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                aria-label={t("settings.accent")}
                style={{
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  border: `1px solid ${tokens.colorNeutralStroke1}`,
                  borderRadius: "4px",
                  background: "transparent",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />
              <Input
                value={hexInput}
                onChange={handleHexChange}
                onBlur={handleHexBlur}
                spellCheck={false}
                maxLength={7}
                aria-label={t("settings.accent")}
                aria-invalid={isInvalid || undefined}
                input={{ style: { fontFamily: "monospace", textTransform: "uppercase" } }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  ...(isInvalid ? { outline: `1px solid ${tokens.colorPaletteRedBorder2}` } : null),
                }}
              />
              {accentColor.toLowerCase() !== DEFAULT_ACCENT_COLOR.toLowerCase() && (
                <Button
                  icon={<ArrowResetRegular />}
                  appearance="subtle"
                  size="small"
                  title={t("settings.accent_reset")}
                  onClick={() => setAccentColor(DEFAULT_ACCENT_COLOR)}
                />
              )}
            </div>
          </div>
        </PopoverSurface>
      </Popover>
    </div>
  );
}
